// src/pages/Overviewcolpagesss/Overviewcolpagesss.js

import React, { useState, useEffect } from "react";
import "./Overviewcolpagesss.css";
import {
  addGrievanceRemarks,
  getOverviewAnalytics,
  grievancePendingData,
} from "../../../api/collectorApi";

import { useTranslation } from "react-i18next";

export default function Overviewcolpagesss() {
  const { t, i18n } = useTranslation();

  const [pendingComplaints, setPendingComplaints] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [priorityAlerts, setPriorityAlerts] = useState([]);
  const [totalGrievances, setTotalGrievances] = useState(0);
  const [resolvedGrievances, setResolvedGrievances] = useState(0);

  const [showListPopup, setShowListPopup] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [editedAction, setEditedAction] = useState("");

  // ================================
  // FETCH OVERVIEW + PENDING LIST
  // ================================
  useEffect(() => {
    async function fetchData() {
      try {
        const overview = await getOverviewAnalytics();
        const pendingList = await grievancePendingData();

        if (overview?.success) {
          setTotalGrievances(overview.data.overview.totalGrievances || 0);
          setResolvedGrievances(overview.data.overview.resolved || 0);
          setPendingCount(overview.data.overview.pending || 0);
          setPriorityAlerts(overview.data.priorityAlerts || []);
        }

        if (pendingList?.success) {
          setPendingComplaints(pendingList.data.grievances || []);
        }
      } catch (error) {
        console.error("Error loading dashboard:", error);
      }
    }

    fetchData();
  }, []);

  // ================================
  // POPUP EVENTS
  // ================================
  const handleOpenListPopup = () => setShowListPopup(true);

  const handleClosePopup = () => {
    setShowListPopup(false);
    setSelectedComplaint(null);
    setEditedAction("");
  };

  const handleComplaintClick = (complaint) => {
    setSelectedComplaint(complaint);
    setEditedAction(complaint.remarks || "");
  };

  // ================================
  // SAVE REMARKS
  // ================================
  const handleSaveAction = async () => {
    if (!editedAction.trim()) {
      alert(t("validation.fillRemarks", { defaultValue: "Please enter remarks" }));
      return;
    }

    const grievanceId = selectedComplaint?.grievanceId;
    const currentStatus = selectedComplaint?.status;

    if (!grievanceId || !currentStatus) {
      alert(t("validation.missingData", { defaultValue: "Missing grievance data" }));
      return;
    }

    try {
      await addGrievanceRemarks(grievanceId, currentStatus, editedAction);

      alert(t("validation.savedSuccess", { defaultValue: "Remarks saved successfully" }));
      handleClosePopup();
    } catch (error) {
      alert(t("validation.saveError", { defaultValue: "Unable to save remarks" }));
    }
  };

  // ================================
  // DATE FORMAT BASED ON LANGUAGE
  // ================================
  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleString(
      i18n.language === "hi"
        ? "hi-IN"
        : i18n.language === "od"
        ? "or-IN"
        : "en-GB",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }
    );
  };

  // ================================
  // JSX
  // ================================
  return (
    <div className="overviewcolpagesss-container">
      <h2 className="overviewcolpagesss-title">
        {t("overview.liveTitle")}
      </h2>

      <div className="overviewcolpagesss-cardwrapper">
        <div className="overviewcolpagesss-card overviewcolpagesss-blue">
          <h3 className="overviewcolpagesss-number">{totalGrievances}</h3>
          <p className="overviewcolpagesss-label">
            {t("overview.totalGrievances")}
          </p>
        </div>

        <div className="overviewcolpagesss-card overviewcolpagesss-green">
          <h3 className="overviewcolpagesss-number">{resolvedGrievances}</h3>
          <p className="overviewcolpagesss-label">
            {t("overview.resolved")}
          </p>
        </div>

        <div
          className="overviewcolpagesss-card overviewcolpagesss-orange overviewcolpagesss-clickable"
          onClick={handleOpenListPopup}
        >
          <h3 className="overviewcolpagesss-number">{pendingCount}</h3>
          <p className="overviewcolpagesss-label">
            {t("overview.pending")}
          </p>
        </div>
      </div>

      {/* ================= Pending List Popup ================= */}
      {showListPopup && !selectedComplaint && (
        <div
          className="overviewcolpagesss-popup-overlay"
          onClick={handleClosePopup}
        >
          <div
            className="overviewcolpagesss-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="overviewcolpagesss-popup-title">
              {t("overview.pendingComplaints")}
            </h3>

            <div className="overviewcolpagesss-popup-list">
              {[...pendingComplaints]
                .sort((a, b) => b.daysOpen - a.daysOpen)
                .map((complaint, index) => (
                  <div
                    key={index}
                    className="overviewcolpagesss-complaint-item"
                    onClick={() => handleComplaintClick(complaint)}
                  >
                    <div className="overviewcolpagesss-complaint-info">
                      <span className="overviewcolpagesss-complaint-number">
                        {complaint.grievanceId}
                      </span>
                      <span className="overviewcolpagesss-complaint-title">
                        {complaint.description}
                      </span>
                    </div>
                    <span className="overviewcolpagesss-complaint-days">
                      {complaint.daysOpen} {t("overview.days")}
                    </span>
                  </div>
                ))}
            </div>

            <button
              className="overviewcolpagesss-closebtn"
              onClick={handleClosePopup}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      )}

      {/* ================= Complaint Details Popup ================= */}
      {selectedComplaint && (
        <div
          className="overviewcolpagesss-popup-overlay"
          onClick={handleClosePopup}
        >
          <div
            className="overviewcolpagesss-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="overviewcolpagesss-popup-title">
              {t("overview.complaintDetails")}
            </h3>

            <div className="overviewcolpagesss-popup-content">
              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("overview.complaintNumber")}
                </span>
                <span className="overviewcolpagesss-value">
                  {selectedComplaint.grievanceId}
                </span>
              </div>

              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("overview.complaintBy")}
                </span>
                <span className="overviewcolpagesss-value">
                  {selectedComplaint.user?.name || "-"}
                </span>
              </div>

              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("analytics.assignedOfficer")}
                </span>
                <span className="overviewcolpagesss-value">
                  {selectedComplaint.assignedOfficer?.name || "-"}
                </span>
              </div>

              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("analytics.createdAt")}
                </span>
                <span className="overviewcolpagesss-value">
                  {formatDate(selectedComplaint.createdAt)}
                </span>
              </div>

              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("report.designation")}
                </span>
                <span className="overviewcolpagesss-value">
                  {selectedComplaint.assignedOfficer?.designation || "-"}
                </span>
              </div>

              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("priority.department")}
                </span>
                <span className="overviewcolpagesss-value">
                  {selectedComplaint.department}
                </span>
              </div>

              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("overview.complaintTitle")}
                </span>
                <span className="overviewcolpagesss-value">
                  {selectedComplaint.description}
                </span>
              </div>

              <div className="overviewcolpagesss-row">
                <span className="overviewcolpagesss-key">
                  {t("overview.pendingDays")}
                </span>
                <span className="overviewcolpagesss-value">
                  {selectedComplaint.daysOpen} {t("overview.days")}
                </span>
              </div>

              <div className="overviewcolpagesss-row overviewcolpagesss-action-row">
                <span className="overviewcolpagesss-key">
                  {t("overview.remarks")}
                </span>
                <textarea
                  className="overviewcolpagesss-input"
                  rows="3"
                  value={editedAction}
                  onChange={(e) => setEditedAction(e.target.value)}
                />
              </div>
            </div>

            <div className="overviewcolpagesss-btn-group">
              <button
                className="overviewcolpagesss-savebtn"
                onClick={handleSaveAction}
              >
                {t("overview.saveRemarks")}
              </button>

              <button
                className="overviewcolpagesss-closebtn"
                onClick={handleClosePopup}
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
